import Navigation from '../components/navigation';
import ConnectionStatus from '@/components/connectionStatus';
import ErrorAlert from '@/components/errorAlert';
import HeaderMenu from '@/components/headerMenu';
import Hotkeys from '@/components/hotkeys';
import { LoadingIndicator } from '@/components/loadingIndicator';
import Login from '@/components/login';
import ProviderContainer from '@/components/providerContainer';
import useAuth from '@/hooks/useAuth';
import { authClient } from '@/lib/auth-client';
import tableTheme from '@/theme/tableTheme';
import theme, { appShellHeaderHeight } from '@/theme/theme';
import { AppShell, MantineProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { Outlet, createRootRoute, type ErrorComponentProps } from '@tanstack/react-router';
import { TableSettingsProvider } from 'schummar-table';
import { MantineTableThemeProvider } from 'schummar-table/mantineTheme';
import { TranslationContextProvider } from 'translation';

function RootError(props: ErrorComponentProps) {
  return (
    <MantineProvider theme={theme} defaultColorScheme="dark">
      <ErrorAlert {...props} />
    </MantineProvider>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
  errorComponent: RootError,
});

function RootComponent() {
  return (
    <ProviderContainer
      providers={[
        <TranslationContextProvider key="translation-context-provider" locale={navigator.language} />,
        <MantineProvider key="mantine-provider" theme={theme} defaultColorScheme="dark" />,
        <DatesProvider key="dates-provider" settings={{ locale: 'de' }} />,
        <TableSettingsProvider key="table-settings-provider" theme={tableTheme} />,
        <MantineTableThemeProvider key="mantine-table-theme-provider" />,
        <ModalsProvider key="modals-provider" />,
      ]}
    >
      <App />
    </ProviderContainer>
  );
}

function App() {
  const { data: session, error: authError, isPending } = authClient.useSession();
  const { canRead } = useAuth();

  if (authError) {
    return <ErrorAlert error={authError} />;
  }

  if (isPending) {
    return <LoadingIndicator size="full" />;
  }

  if (!session) {
    return <Login />;
  }

  if (!canRead) {
    return <ErrorAlert error={'You do not have permission to access the application. Please contact an administrator.'} />;
  }

  return (
    <>
      <AppShell header={{ height: appShellHeaderHeight }}>
        <AppShell.Header
          css={{
            display: 'flex',
          }}
        >
          <Navigation />

          <div css={{ flex: 1 }} />

          <HeaderMenu />
        </AppShell.Header>

        <AppShell.Main
          css={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Outlet />
        </AppShell.Main>
      </AppShell>

      <Hotkeys />
      <Notifications />
      <ConnectionStatus />
    </>
  );
}
